import React, { useEffect, useState } from "react";
import { jobsApi, aiApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { JOBS } from "@/constants/testIds";
import { toast } from "sonner";
import { Briefcase, MapPin, Sparkles, Plus, Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth";

export default function Jobs() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", client: "", location: "", description: "", skills: "" });
  const [drafting, setDrafting] = useState(false);
  const canPost = user && (user.role === "recruiter" || user.role === "client" || user.role === "admin");

  useEffect(() => { jobsApi.list().then(setJobs); }, []);

  const draftJD = async () => {
    if (!form.title) { toast.error("Enter a title first"); return; }
    setDrafting(true);
    try {
      const res = await aiApi.jd(`Job title: ${form.title}. Client: ${form.client || "TBD"}. Location: ${form.location || "TBD"}. Skills: ${form.skills}.`);
      setForm({ ...form, description: res.draft });
      toast.success("AI drafted the JD. Review before publishing.");
    } catch (e) {
      toast.error("AI error: " + (e?.response?.data?.detail || e.message));
    } finally { setDrafting(false); }
  };

  const submit = async () => {
    try {
      const payload = { ...form, skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean) };
      const created = await jobsApi.create(payload);
      setJobs([created, ...jobs]);
      setOpen(false);
      setForm({ title: "", client: "", location: "", description: "", skills: "" });
      toast.success("Job posted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-1">Requisitions</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Jobs</h1>
        </div>
        {canPost && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid={JOBS.newJobBtn} className="bg-[#0047AB] hover:bg-[#003380] gap-2"><Plus className="h-4 w-4" /> New Job</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl tracking-tight">Post a new job</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Title</Label>
                    <Input data-testid={JOBS.jobTitleInput} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-2" placeholder="Senior Backend Engineer" />
                  </div>
                  <div>
                    <Label>Client</Label>
                    <Input data-testid={JOBS.jobClientInput} value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value })} className="mt-2" placeholder="Northwind Robotics" />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Location</Label>
                    <Input data-testid={JOBS.jobLocationInput} value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-2" placeholder="Remote · US" />
                  </div>
                  <div>
                    <Label>Skills (comma-separated)</Label>
                    <Input data-testid={JOBS.jobSkillsInput} value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })} className="mt-2" placeholder="Python, FastAPI, PostgreSQL" />
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label>Description</Label>
                    <Button data-testid={JOBS.aiDraftJdBtn} type="button" size="sm" variant="outline" onClick={draftJD} disabled={drafting} className="gap-1.5 border-indigo-200 text-indigo-700 hover:bg-indigo-50">
                      {drafting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />} AI draft
                    </Button>
                  </div>
                  <Textarea data-testid={JOBS.jobDescriptionInput} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-2 min-h-40" placeholder="Describe the role, responsibilities, and impact…" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                <Button data-testid={JOBS.jobSubmit} onClick={submit} className="bg-[#0047AB] hover:bg-[#003380]">Publish job</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {jobs.map((j) => (
          <Card key={j.id} data-testid={JOBS.jobRow(j.id)} className="border-border shadow-none p-5 hover:border-[#0047AB] transition-colors">
            <div className="flex items-start justify-between gap-3">
              <div className="h-10 w-10 rounded-md bg-[#0047AB]/10 text-[#0047AB] grid place-items-center"><Briefcase className="h-5 w-5" /></div>
              <Badge variant="outline" className="text-[10px] font-mono uppercase tracking-wider">{j.status}</Badge>
            </div>
            <div className="mt-4">
              <h3 className="font-display font-bold tracking-tight text-lg leading-tight">{j.title}</h3>
              <div className="text-sm text-muted-foreground mt-1">{j.client}</div>
            </div>
            <div className="mt-3 flex items-center gap-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {j.location}</span>
              <span>· {j.employment_type}</span>
            </div>
            <div className="mt-4 flex flex-wrap gap-1.5">
              {j.skills.slice(0, 4).map((s) => (
                <Badge key={s} variant="secondary" className="text-[10px] font-mono">{s}</Badge>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-border text-sm font-mono">
              ${(j.salary_min / 1000).toFixed(0)}k – ${(j.salary_max / 1000).toFixed(0)}k
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
